<template>
  <el-dialog
    :visible="visible"
    top="0"
    :append-to-body="needToBody"
    :close-on-click-modal="false"
    :before-close="handleClose"
    :show-close="false"
    center
  >
    <el-carousel
      ref="bannerCarousel"
      class="aspect-[654/900] md:aspect-[700/500]"
      :height="'100%'"
      :arrow="bannerItems.length > 1 ? 'always' : 'never'"
      indicator-position="none"
      @change="handleChangeCarouselSlide"
    >
      <el-carousel-item v-for="item in bannerItems" :key="item.key">
        <component :ref="item.key" :is="item.component" v-bind="item.props" />
      </el-carousel-item>
    </el-carousel>
    <img
      v-if="needCloseButton"
      class="closeButton"
      src="@/assets/images/close2.png"
      data-testid="closeImg"
      @click="handleClose"
    />
  </el-dialog>
</template>

<script>
import { apiGetAbTestingEligible, apiGetBanners, apiSnoozeBanner, apiSubmitAbTesting } from '@/resource';
import { PopupBannerModel, getPopupBannerDisplayPageEnumFromRoutePath } from '@/constants/popupBanner';
import { AbTemplateDisplayPage, AbTemplateRule } from '@/constants/abTesting';
import PopupContent from './popupContent/PopupContent.vue';

export default {
  name: 'PopupBanner',
  props: {
    needToBody: { type: Boolean, default: false },
    needCloseButton: { type: Boolean, default: true }
  },
  data() {
    return {
      visible: false,
      bannerItems: [],
      bannerIndexShown: [],
      bannersToSnooze: [],
      abTestingTemplates: []
    };
  },
  computed: {
    routePath() {
      return this.$route.path;
    },
    countryCode() {
      return parseInt(this.$store.state.common.countryCode);
    },
    salesCompany() {
      return this.$store.state.common.salesCompany;
    },
    // Props for getBanners()
    bannersWatcherProps() {
      return { routePath: this.routePath, countryCode: this.countryCode };
    },
    lang() {
      return this.$store.state.common.lang;
    }
  },
  watch: {
    bannerItems(bannerItems) {
      if (bannerItems.length > 0) {
        this.visible = true;
      }
    },
    bannersWatcherProps() {
      this.init();
    }
  },
  mounted() {
    this.init();
  },
  methods: {
    // isArabianLang() {
    //   return this.$store.state.common.lang === 'ar';
    // },
    async init() {
      await this.getAbTestingTemplates();
      this.setBanners({ countryCode: this.countryCode, routePath: this.routePath });
    },
    async setBanners({ countryCode, routePath }) {
      const displayPage = getPopupBannerDisplayPageEnumFromRoutePath(routePath);
      const newBanners = displayPage !== null ? await this.getBanners(countryCode, displayPage) : [];

      // Filter abTestingTemplates
      if (newBanners.length === 0) {
        this.abTestingTemplates = [];
        this.bannerIndexShown = [];
      }

      // Init bannerIndexShown
      if (newBanners.length > 0) {
        this.bannerItems = newBanners.sort(({ priority: a = null }, { priority: b = null }) => {
          // Handle null values as lowest priority
          if (a === null && b === null) {
            return 0;
          } else if (a === null) {
            return 1; // a (null) has lower priority
          } else if (b === null) {
            return -1; // b (null) has lower priority
          }

          // Both a and b are not null, sort by their numerical value
          return a - b;
        });

        // Reset to 0 when routePath changed
        this.$refs.bannerCarousel?.setActiveItem(0);
        this.bannerIndexShown = [0];
      }
    },
    async getBanners(countryCode, displayPage) {
      try {
        const res = await apiGetBanners({ country: countryCode, displayPage: displayPage });

        if (res.status === 200 && res.data.success && res?.data?.rows?.length > 0) {
          const banners = res.data.rows;

          return banners.map(e => {
            const addRemoveSnoozeBanner = e.snoozeDuration ? action => this.addRemoveSnoozeBanner(e.id, action) : null;
            let getLangBanner = e.bannerDocuments.filter(f => f.languageCode === this.lang).filter(h => h.isDel === 0);

            if (getLangBanner.length == 0) {
              getLangBanner = e.bannerDocuments.filter(f => f.languageCode === 'en_US');
            }

            let abTemplates = [];
            if (e.campaign?.length > 0) {
              // Campaign banner shows corresponding campaign templates
              const campaignIds = e.campaign?.split(',') ?? [];
              abTemplates = this.abTestingTemplates.filter(template => campaignIds.includes(template.campaignId));
            } else if (e.country?.length > 0) {
              // Country banner shows all templates
              abTemplates = this.abTestingTemplates;
            }

            return new PopupBannerModel({
              ...e,
              key: `banner-${e.id}`,
              component: PopupContent,
              props: {
                imagePath: `api/${getLangBanner[0].webDocument}`,
                mobileImagePath: `api/${getLangBanner[0].appDocument}`,
                callToActionLink: getLangBanner[0].callToActionLink ? getLangBanner[0].callToActionLink : '',
                addRemoveSnoozeBanner: addRemoveSnoozeBanner,
                abTestingTemplates: abTemplates,
                setAbTestingTemplate: this.setAbTestingTemplate
              },
              displayPage: e.displayPage ? e.displayPage.split(',').map(e => Number(e)) : [],
              excludeCountry: null,
              country: e.country ? e.country.split(',').map(e => Number(e)) : []
            });
          });
        }

        return [];
      } catch (e) {
        console.log(e);
        return [];
      }
    },
    async getAbTestingTemplates() {
      try {
        const res = await apiGetAbTestingEligible(AbTemplateDisplayPage.POP_UP);
        if (res.status === 200 && res.data?.data?.length > 0) {
          this.abTestingTemplates = res.data.data.map(template => {
            const {
              id,
              templateName,
              targetAudienceType,
              displayPage,
              rules,
              campaignId,
              campaignName,
              targetContentSetting
            } = template;

            return {
              id,
              templateName,
              targetAudienceType,
              displayPage,
              rules,
              campaignId,
              campaignName,
              targetContentSetting,
              checked: rules === AbTemplateRule.CLICKED
            };
          });
        } else {
          this.abTestingTemplates = [];
        }
      } catch (e) {
        console.log(e);
        this.abTestingTemplates = [];
      }
    },
    setAbTestingTemplate(id, checked) {
      let template = this.abTestingTemplates.find(template => template.id === id) ?? null;
      if (template) {
        template.checked = checked;
        this.bannerItems.forEach(banner => {
          if (banner.props?.abTestingTemplates) {
            let bannerTemplate = banner.props.abTestingTemplates.find(e => e.id === id);
            if (bannerTemplate) {
              bannerTemplate.checked = checked;
            }
          }
        });
      }
    },
    handleClose() {
      this.visible = false;
      if (this.bannersToSnooze.length > 0) {
        apiSnoozeBanner(this.bannersToSnooze);
      }
      if (this.abTestingTemplates.length > 0 && this.bannerIndexShown.length > 0) {
        const abTestingTemplatesShown = this.bannerItems
          .filter((e, i) => this.bannerIndexShown.includes(i) && e.props?.abTestingTemplates?.length > 0)
          .map(e => e.props.abTestingTemplates);

        const abTestingTemplateIdsShown = [...new Set(abTestingTemplatesShown.flat())].map(e => e.id);

        apiSubmitAbTesting(
          this.abTestingTemplates
            .filter(e => abTestingTemplateIdsShown.includes(e.id))
            .map(e => {
              return {
                templateId: e.id,
                templateName: e.templateName,
                targetAudienceType: e.targetAudienceType,
                displayPage: AbTemplateDisplayPage.POP_UP,
                rules: e.rules,
                campaignId: e.campaignId,
                campaignName: e.campaignName,
                status: e.checked ? AbTemplateRule.CLICKED : AbTemplateRule.UN_CLICKED
              };
            })
        );
      }

      this.$emit('readGuide', 'fromPopUp');
    },
    handleChangeCarouselSlide(index) {
      if (!this.bannerIndexShown.includes(index)) {
        this.bannerIndexShown.push(index);
      }
    },
    addRemoveSnoozeBanner(id, action) {
      switch (action) {
        case 'add':
          this.bannersToSnooze.push(id);
          break;
        case 'remove':
          this.bannersToSnooze = this.bannersToSnooze.filter(e => e !== id);
          break;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@/assets/css/components/home/PopupBanner.scss';

.closeButton {
  @include rtl-sass-prop(right, left, 0.625rem);
}

@media (max-width: 769px) {
  /deep/ .el-dialog {
    max-width: 450px;
  }
}
</style>

<style lang="scss">
.closeButton {
  position: absolute;
  top: 0.625rem;
  cursor: pointer;
  z-index: 99;
  max-width: 30px;
  @include rtl-sass-prop(right, left, 12px);
}
</style>
